import React, { useState, useEffect, useMemo, useCallback } from "react";
import { CreditCard, Check, Trash2, ExternalLink } from "lucide-react";
import SubscriptionStatus from "../SubscriptionStatus";
import UsageSection from "./UsageSection";
import { getAuthInstance } from "../../firebase";
import { loadStripe } from "@stripe/stripe-js";
import {
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  Elements,
  ElementsConsumer,
} from "@stripe/react-stripe-js";
import {
  capitalizeFirstCharacter,
  processFeatureText,
} from "../../utils/helper";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { AppLoader } from "../AppLoader";

const auth = getAuthInstance();
const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null;

const API_URL = process.env.REACT_APP_API_URL;

export const getPlanDescription = (plan) => {
  if (!plan.limits) return "";

  if (plan.limits.responses === null || plan.limits.responses === undefined) {
    return "Unlimited responses/mo";
  }

  if (plan.name === "Free") {
    return `${plan.limits.responses} responses/mo`;
  }

  return `${plan.limits.responses.toLocaleString()} responses/mo included`;
};

const SuccessModal = ({ isOpen, onClose, planName }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      <div
        className="relative bg-white p-8 rounded-3xl shadow-xl max-w-[480px] w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-6 top-6 text-gray-400 hover:text-gray-600"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#ECFDF3] rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-[#027A48]" />
          </div>
          <h3 className="text-3xl font-medium mb-2 font-['Inter']">
            Success!
          </h3>
          <p className="text-gray-500">
            Your subscription has been successfully updated to the {planName}{" "}
            plan.
            <br />
            The changes are now active.
          </p>
        </div>

        <button
          onClick={onClose}
          className="w-full h-14 text-lg rounded-xl bg-black text-white hover:bg-black/90 transition-all font-medium"
        >
          Got it
        </button>
      </div>
    </div>
  );
};

const ErrorModal = ({ isOpen, onClose, error }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg p-6 w-96">
        <h3 className="text-lg font-semibold mb-4">Error</h3>
        <p className="text-sm text-red-600 mb-4">{error}</p>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-black text-white rounded-md hover:bg-black/90"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Memoize the feature display component
export const FeatureText = React.memo(({ feature, limits, checkClassName }) => {
  const processedText = processFeatureText(feature, limits);
  if (!checkClassName) {
    return (
      <div className="flex items-start text-app-gray-500 gap-2">
        <Check className="w-4 h-4 flex-shrink-0" />
        <span className="text-xs font-normal">{processedText}</span>
      </div>
    );
  }

  return (
    <div className="flex items-start text-[#303236] gap-2.5">
      <span className={`mt-[1px] w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 ${checkClassName}`}>
        <Check className="w-3 h-3" strokeWidth={3} />
      </span>
      <span className="text-[12px] leading-[18px] font-normal">{processedText}</span>
    </div>
  );
});

const PLAN_ACCENTS = {
  Free: {
    badge: "bg-[#E9EAEB] text-[#4B4F55]",
    check: "bg-[#8F8F92] text-white",
    button: "border border-[#E5E5E5] bg-[#F7F7F7] text-[#303236] hover:bg-[#EFEFEF]",
    currentButton: "border border-[#DADDDD] bg-[#F5F7F7] text-[#303236]",
  },
  Pro: {
    badge: "bg-[#FAD1A5] text-[#6B3B14]",
    check: "bg-[#F8993F] text-white",
    button: "bg-[#F8993F] text-[#1F1F1F] hover:bg-[#F28D2D]",
    currentButton: "border border-[#F3CDA5] bg-[#FFF7EE] text-[#6B3B14]",
  },
  Growth: {
    badge: "bg-[#BFF4DF] text-[#155A43]",
    check: "bg-[#34D399] text-white",
    button: "border border-[#E5ECEA] bg-[#F3FAF8] text-[#26312F] hover:bg-[#E8F4F1]",
    currentButton: "border border-[#BDEBDC] bg-[#F1FCF8] text-[#155A43]",
  },
  Enterprise1: {
    badge: "bg-[#2F3434] text-white",
    check: "bg-[#2F3434] text-white",
    button: "bg-[#2F3434] text-white hover:bg-[#1a1f1f]",
    currentButton: "border border-[#2F3434] bg-[#F5F7F7] text-[#2F3434]",
  },
};

const getPlanAccent = (name) => PLAN_ACCENTS[name] || PLAN_ACCENTS.Growth;

const PLAN_RANK = { free: 0, pro: 1, growth: 2, enterprise1: 3 };
const getPlanRank = (name) => PLAN_RANK[name?.toLowerCase()] ?? -1;

const BILLING_CARD_CLASS =
  "rounded-[22px] border border-[#E8E8E8] bg-white/95 shadow-[0_8px_30px_rgba(21,27,38,0.035)]";

const PlanCard = ({
  id,
  name,
  price,
  features,
  isAnnual,
  onSubscribe,
  currentSubscription,
  limits,
  pricingOptions,
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const selectedPricingOption = isAnnual
    ? pricingOptions?.annual
    : pricingOptions?.monthly;
  const canSelectPlan = name === "Free" || Boolean(selectedPricingOption);
  const accent = getPlanAccent(name);
  const annualTotal = pricingOptions?.annual?.amount;
  const showAnnualBillingNote = name !== "Free" && isAnnual && annualTotal;

  const isDowngrade =
    getPlanRank(name) < getPlanRank(currentSubscription?.plan?.type);

  // Determine if this is the current plan
  const isCurrent = useMemo(() => {
    const currentPlanName = currentSubscription?.plan?.type;
    const thisPlanName = name.toLowerCase();

    if (thisPlanName === "free") {
      // For free plan, only check the name
      return currentPlanName === "free";
    }

    // For paid plans, check both name and interval
    return (
      currentPlanName === thisPlanName &&
      currentSubscription?.plan?.interval === (isAnnual ? "year" : "month")
    );
  }, [currentSubscription, name, isAnnual]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onSubscribe(id, selectedPricingOption);
    } catch (err) {
      setError(
        err.message || "Failed to update subscription. Please try again."
      );
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`mt-3 md:mt-0 w-full min-h-[352px] rounded-[22px] border border-[#E8E8E8] bg-white/95 relative overflow-hidden
      shadow-[0_8px_30px_rgba(21,27,38,0.035)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_45px_rgba(21,27,38,0.08)] ${
        isCurrent ? "ring-1 ring-[#D8D8D8]" : ""
      }`}
    >
      <div className="flex h-full min-h-[352px] flex-col px-5 py-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <span className={`rounded-full px-3 py-1.5 text-sm font-medium ${accent.badge}`}>
            {name === "Enterprise1" ? "Enterprise" : name}
          </span>
          {name === "Growth" && (
            <span className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[#D28A22]">
              Best
            </span>
          )}
        </div>

        <div className="mb-4">
          <p className="text-[13px] font-medium text-[#4B4F55]">
            {getPlanDescription({ name, limits })}
          </p>
          {canSelectPlan && (
            <div className="mt-2 flex items-end gap-1">
              <span className="pb-1 text-[17px] font-semibold text-[#2A2D31]">$</span>
              <span className="text-[30px] font-semibold leading-none tracking-[-0.04em] text-[#2A2D31]">
                {price}
              </span>
              <span className="pb-1 text-[11px] font-medium text-[#4B4F55]">
                /mo*
              </span>
            </div>
          )}

          <p className="mt-4 min-h-[34px] text-[12px] leading-[17px] text-[#5E6268]">
            {name === "Free"
              ? "For individuals just getting started"
              : name === "Pro"
              ? "For professionals who need more power and customization"
              : name === "Enterprise1"
              ? "For organizations that need unlimited power and dedicated support"
              : "For teams ready to scale with higher limits and priority support"}
          </p>
        </div>

        <div className="mb-5 min-h-[16px] text-center text-[10px] text-[#777C82]">
          {showAnnualBillingNote
            ? `Billed annually at $${annualTotal}`
            : name === "Free"
            ? ""
            : "Billed monthly"}
        </div>

        {canSelectPlan && !isDowngrade && (
          <button
            className={`w-full h-10 rounded-lg text-[12px] font-medium transition-colors overflow-hidden
            shadow-[inset_0_0_0_1px_rgba(255,255,255,0.25)] ${
              isCurrent
                ? accent.currentButton
                : accent.button
            }`}
            disabled={isCurrent || isLoading}
            onClick={handleConfirm}
          >
            {isLoading ? (
              <AppLoader size={6} className="mx-auto" />
            ) : isCurrent ? (
              <div>
                Current plan
                {currentSubscription.scheduledChanges && (
                  <div className="text-xs text-app-gray-500 font-thin">
                    Valid till{" "}
                    {new Date(
                      currentSubscription.scheduledChanges.plan.effectiveDate
                    ).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                )}
              </div>
            ) : (
              `Get ${name === "Enterprise1" ? "Enterprise" : name}`
            )}
          </button>
        )}

        {/* Features List */}
        <div className="mt-5 space-y-2.5">
          {features.map((feature, index) => (
            <FeatureText key={index} feature={feature} limits={limits} checkClassName={accent.check} />
          ))}
        </div>
      </div>

      {/* Only show SuccessModal for Free plan */}
      <SuccessModal
        isOpen={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          setTimeout(() => {
            window.location.reload();
          }, 0);
        }}
        planName={name}
      />

      <ErrorModal
        isOpen={showError}
        onClose={() => setShowError(false)}
        error={error}
      />
    </div>
  );
};

const AddPaymentForm = ({ onClose, customerName, customerEmail }) => {
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e, stripe, elements) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      if (!stripe || !elements) {
        throw new Error("Stripe has not loaded yet");
      }

      const cardElement = elements.getElement(CardNumberElement);
      const { token, error } = await stripe.createToken(cardElement, {
        name: customerName,
      });

      if (error) {
        throw new Error(error.message);
      }

      // Send token to backend
      const response = await fetch(`${API_URL}/api/stripe/add-payment-method`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
        },
        body: JSON.stringify({
          token: token.id,
          customerName,
          customerEmail,
          userId: auth.currentUser.uid,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to add payment method");
      }

      const data = await response.json();
      if (data.success) {
        onClose();
      }
    } catch (err) {
      console.error("Error adding payment method:", err);
      setError(
        err.message || "Failed to add payment method. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ElementsConsumer>
      {({ stripe, elements }) => (
        <form
          onSubmit={(e) => handleSubmit(e, stripe, elements)}
          className="space-y-4"
        >
          {error && <div className="text-red-500 text-sm">{error}</div>}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Card Number
              </label>
              <div className="border border-gray-300 rounded-md p-3">
                <CardNumberElement />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiry Date
                </label>
                <div className="border border-gray-300 rounded-md p-3">
                  <CardExpiryElement />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CVC
                </label>
                <div className="border border-gray-300 rounded-md p-3">
                  <CardCvcElement />
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm bg-black text-white rounded-md hover:bg-black/90 relative overflow-hidden"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <AppLoader size={4} />
                </div>
              ) : (
                "Add Card"
              )}
            </button>
          </div>
        </form>
      )}
    </ElementsConsumer>
  );
};

const AddPaymentModal = ({ isOpen, onClose, customerName, customerEmail }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96">
        <h3 className="text-lg font-semibold mb-4">Add Payment Method</h3>
        {!stripePromise ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Stripe payments are not configured for this environment.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="w-full bg-gray-900 text-white py-2 rounded-md hover:bg-gray-800 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
        <Elements stripe={stripePromise}>
          <AddPaymentForm
            onClose={onClose}
            customerName={customerName}
            customerEmail={customerEmail}
          />
        </Elements>
        )}
      </div>
    </div>
  );
};

const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  cardDetails,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setIsLoading(true);
    await onConfirm();
    setIsLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96">
        <h3 className="text-lg font-semibold mb-4">Confirm Deletion</h3>
        <p className="text-sm text-gray-600 mb-4">
          Are you sure you want to remove this card?
          <br />
          <span className="font-medium">
            {cardDetails.brand} ending in {cardDetails.last4}
          </span>
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 relative overflow-hidden"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <AppLoader size={4} />
              </div>
            ) : (
              "Delete Card"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const BillingTab = ({ usingFor = "BILLING" }) => {
  const { currentSubscription } = useSelector((state) => state.app);
  const [isAnnual, setIsAnnual] = useState(true);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    cardId: null,
    cardDetails: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [rawPlans, setRawPlans] = useState([]);

  // Memoize the plan transformation logic
  const transformPlans = useCallback((plans, isAnnual, currentSub) => {
    if (!plans.length) return [];

    return plans.map((plan) => ({
      id: plan._id,
      name: plan.name,
      price:
        plan.name === "Free"
          ? 0
          : isAnnual
          ? // For annual plans, divide by 12 to show monthly equivalent
            Math.round((plan.pricing?.annual?.amount / 12) * 100) / 100
          : plan.pricing?.monthly?.amount,
      current:
        plan.name === "Free"
          ? !currentSub || currentSub.plan?.type === "free"
          : currentSub?.plan?.type === plan.name?.toLowerCase() &&
            ((isAnnual && currentSub?.plan?.interval === "year") ||
              (!isAnnual && currentSub?.plan?.interval === "month")),
      color:
        plan.name === "Free"
          ? "bg-[#FFFEF2]"
          : plan.name === "Pro"
          ? "bg-gradient-to-b from-[#EFFBFF] to-[#FEE135]"
          : "bg-gradient-to-b from-[#F4FAFA] to-[#77F7C8]",
      features: plan.features.map((feature) =>
        processFeatureText(feature, plan.limits)
      ),
      limits: plan.limits,
      paymentLink:
        plan.name === "Free"
          ? null
          : isAnnual
          ? plan.pricing?.annual?.paymentLink
          : plan.pricing?.monthly?.paymentLink,
      pricingOptions: plan.pricing || null,
    }));
  }, []);

  // Update plans when isAnnual changes
  useEffect(() => {
    const transformed = transformPlans(rawPlans, isAnnual, currentSubscription);
    setSubscriptionPlans(transformed);
  }, [isAnnual, rawPlans, currentSubscription, transformPlans]);

  // Fetch subscription plans
  const fetchSubscriptionPlans = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscription-plans`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (!data.plans) return;
      const currentRank = getPlanRank(currentSubscription?.plan?.type);
      const visiblePlans = data.plans.filter((p) => {
        const planRank = getPlanRank(p.name);

        if (p.name === "Enterprise1") {
          return currentRank >= getPlanRank("growth");
        }

        if (p.name.includes("Enterprise")) {
          const currentPlanName = currentSubscription?.plan?.type
            ? capitalizeFirstCharacter(currentSubscription.plan.type)
            : "";
          return currentPlanName === p.name;
        }

        if (planRank === -1) return p.isPublic !== false;

        return planRank >= currentRank;
      });
      if (visiblePlans && visiblePlans.length > 0) {
        setRawPlans(visiblePlans);
        const transformed = transformPlans(
          visiblePlans,
          isAnnual,
          currentSubscription
        );

        setSubscriptionPlans(transformed);
      }
    } catch (error) {
      console.error("Error fetching subscription plans:", error);
    }
  }, [isAnnual, currentSubscription, transformPlans]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const promises = [fetchSubscriptionPlans()];
      
      // Only fetch payment methods and transactions for non-free users
      if (currentSubscription?.plan?.type !== "free") {
        promises.push(fetchPaymentMethods(), fetchTransactions());
      }
      
      await Promise.all(promises);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsLoading(false);
  };
  useEffect(() => {
    fetchData();
  }, [currentSubscription]);

  const fetchPaymentMethods = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/stripe/payment-methods`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setPaymentMethods(data.paymentMethods);
    } catch (err) {
      console.error("Error fetching payment methods:", err);
    }
  };

  const fetchTransactions = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/stripe/transactions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setTransactions(data.transactions);
    } catch (err) {
      console.error("Error fetching transactions:", err);
    }
  };

  const handleRemovePaymentMethod = async (paymentMethodId) => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(
        `${API_URL}/api/stripe/remove-payment-method`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ paymentMethodId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to remove payment method");
      }

      setDeleteConfirmation({ show: false, cardId: null, cardDetails: null });
      // Refresh payment methods list
      fetchPaymentMethods();
    } catch (err) {
      console.error("Error removing payment method:", err);
    }
  };

  const handleSubscribe = async (planId, pricingObject) => {
    try {
      const token = await auth.currentUser.getIdToken();

      if (!pricingObject) {
        if (currentSubscription?.subscriptionId) {
          const response = await fetch(
            `${API_URL}/api/stripe/create-portal-session`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({}),
            }
          );
          if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || "Failed to open billing portal.");
          }
          const { url } = await response.json();
          window.location.href = url;
          return;
        }

        const response = await fetch(`${API_URL}/api/stripe/subscribe`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || "Failed to downgrade subscription.");
        }
        window.location.reload();
        return;
      }

      if (currentSubscription?.subscriptionId) {
        const response = await fetch(
          `${API_URL}/api/stripe/create-portal-session`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              configurationId: pricingObject.configurationId,
              priceId: pricingObject.priceId,
            }),
          }
        );
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || "Failed to open billing portal.");
        }
        const { url } = await response.json();
        window.location.href = url;
        return;
      }

      const response = await fetch(
        `${API_URL}/api/stripe/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            priceId: pricingObject.priceId,
          }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || "Failed to create checkout session.");
      }
      const { url } = await response.json();
      window.location.href = url;
    } catch (err) {
      toast.error(
        err.message || "Failed to update subscription. Please try again later."
      );
      throw err;
    }
  };

  // Update isAnnual when subscription data is loaded
  useEffect(() => {
    if (currentSubscription?.plan?.interval) {
      setIsAnnual(currentSubscription?.plan?.interval === "year");
    }
  }, [currentSubscription]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <AppLoader coverage="self" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fadeIn">
      {usingFor === "BILLING" && (
        <div className={`${BILLING_CARD_CLASS} p-5`}>
          <SubscriptionStatus
            handleSubscribe={handleSubscribe}
            subscription={currentSubscription}
            isAnnual={isAnnual}
            subscriptionPlans={subscriptionPlans}
          />
        </div>
      )}

      {usingFor === "BILLING" && <UsageSection />}

      {/* Plans */}
      <div className="space-y-4">
        <div className={`flex flex-col gap-3`}>
          <div
            className={`md:flex items-center ${
              usingFor === "BILLING" ? "justify-between" : "justify-center"
            }`}
          >
            {usingFor === "BILLING" && (
              <h3 className="text-lg text-app-black md:font-semibold">
                Available Plans
              </h3>
            )}

            {/* Billing Toggle and Manage Subscription */}
            <div className="mt-3 md:mt-0 flex items-center gap-4">
              <div className="inline-flex h-9 items-center rounded-full border border-[#EEF0F0] bg-[#F7F9F8] p-1 shadow-[0_6px_16px_rgba(18,24,31,0.04)] relative">
                {/* Animated Background */}
                <div
                  className="absolute top-1 h-7 rounded-full bg-white shadow-[0_2px_8px_rgba(18,24,31,0.08)] transition-all duration-300 ease-out"
                  style={{
                    width: isAnnual ? "96px" : "116px",
                    left: isAnnual ? "4px" : "100px",
                  }}
                />

                {/* Toggle Buttons */}
                <div className="relative z-10 flex items-center">
                  <button
                    className={`w-24 rounded-full py-1.5 text-center text-[11px] font-medium transition-colors duration-300 cursor-pointer
                      ${isAnnual ? "text-[#2A2D31]" : "text-[#8A8F94]"}`}
                    onClick={() => setIsAnnual(true)}
                  >
                    Yearly 30% off
                  </button>

                  <button
                    className={`w-[116px] rounded-full py-1.5 text-center text-[11px] font-medium transition-colors duration-300 cursor-pointer
                      ${!isAnnual ? "text-[#2A2D31]" : "text-[#8A8F94]"}`}
                    onClick={() => setIsAnnual(false)}
                  >
                    Monthly pricing
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Scheduled Change Notice */}
          {currentSubscription?.scheduledChanges?.plan?.type && (
            <div
              className={`text-sm text-app-gray-600 ${
                usingFor === "VIEW-PLANS" ? "text-center" : ""
              }`}
            >
              Your subscription will change to{" "}
              <span className="font-semibold text-app-black">
                {capitalizeFirstCharacter(
                  currentSubscription.scheduledChanges.plan.type
                )}{" "}
                {currentSubscription.scheduledChanges.plan.type !== "free"
                  ? currentSubscription.scheduledChanges.plan.interval
                  : ""}
                {currentSubscription.scheduledChanges.plan.type !== "free" &&
                currentSubscription.scheduledChanges.plan.interval
                  ? "ly"
                  : ""}
              </span>{" "}
              plan on{" "}
              <span className="font-semibold text-app-black">
                {new Date(
                  currentSubscription.scheduledChanges.plan.effectiveDate
                ).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
              .
            </div>
          )}
        </div>

        {/* Mobile view - Pro, Growth, Free order */}
        <div className="block md:hidden space-y-3">
          {[...subscriptionPlans]
            .sort((a, b) => {
              const order = { Pro: 1, Growth: 2, Free: 3 };
              return (order[a.name] || 999) - (order[b.name] || 999);
            })
            .map((plan) => (
              <PlanCard
                key={plan.id}
                {...plan}
                isAnnual={isAnnual}
                onSubscribe={handleSubscribe}
                currentSubscription={currentSubscription}
                limits={plan.limits}
              />
            ))}
        </div>

        {/* Desktop view - Original order */}
        <div className={`hidden md:grid gap-6 ${subscriptionPlans.length <= 2 ? "grid-cols-2" : "grid-cols-3"}`}>
          {subscriptionPlans.map((plan) => (
            <PlanCard
              key={plan.id}
              {...plan}
              isAnnual={isAnnual}
              onSubscribe={handleSubscribe}
              currentSubscription={currentSubscription}
              limits={plan.limits}
            />
          ))}
        </div>
      </div>

      {/* Manage / Cancel link for paid users */}
      {currentSubscription && currentSubscription?.plan?.type !== "free" && (
        <div className="mt-5 text-center text-[13px] text-[#8A8F94]">
          Need to cancel or change your plan?{" "}
          <button
            onClick={async () => {
              try {
                const token = await auth.currentUser.getIdToken();
                const response = await fetch(`${API_URL}/api/stripe/create-portal-session`, {
                  method: "POST",
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({}),
                });
                if (!response.ok) throw new Error("Failed to open billing portal.");
                const { url } = await response.json();
                window.location.href = url;
              } catch (err) {
                toast.error("Unable to open billing portal. Please try again.");
              }
            }}
            className="underline underline-offset-2 text-[#5E6268] hover:text-[#303236] transition-colors"
          >
            Manage subscription
          </button>
        </div>
      )}

      {/* User-specific sections - Only show when usingFor is BILLING and not on free plan */}
      {usingFor === "BILLING" && currentSubscription?.plan?.type !== "free" && (
        <>
          {/* Payment Methods Section */}
          <div className={`${BILLING_CARD_CLASS} p-5 mt-8`}>
            <h3 className="text-lg font-semibold text-[#2A2D31] mb-4">
              Payment Methods
            </h3>
            <div className="space-y-3">
              {paymentMethods.length > 0 ? (
                <div className="space-y-2.5">
                  {paymentMethods?.map((method) => (
                    <div
                      key={method.id}
                      className="flex items-center justify-between rounded-[18px] border border-[#EEEEEE] bg-white px-3.5 py-3"
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F7F9F8] text-[#5E6268]">
                          <CreditCard className="w-4 h-4" />
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[#303236] capitalize">
                            {method.brand}
                          </span>
                          <span className="text-sm font-medium text-[#303236]">
                            •••• {method.last4}
                          </span>
                          <span className="text-xs text-[#8A8F94]">
                            {String(method.expMonth).padStart(2, "0")}/
                            {String(method.expYear).slice(-2)}
                          </span>
                          {method.isDefault && (
                            <span className="text-xs bg-[#F1FCF8] text-[#155A43] px-2 py-0.5 rounded-full">
                              Default
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          setDeleteConfirmation({
                            show: true,
                            cardId: method.id,
                            cardDetails: { brand: method.brand, last4: method.last4 },
                          })
                        }
                        className="text-[#A0A4A8] hover:text-app-red-400 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-[18px] border border-[#EEEEEE] bg-white px-3.5 py-3 text-sm text-[#777C82]">
                  No payment methods added yet
                </div>
              )}
              <button
                className="flex items-center gap-2 text-[#5E6268] hover:text-[#303236] font-semibold text-sm mt-3"
                onClick={() => setShowAddPayment(true)}
              >
                <CreditCard className="w-4 h-4" />
                <span>Add Payment Method</span>
              </button>
            </div>
          </div>

          {/* Billing History Section */}
          <div className={`${BILLING_CARD_CLASS} p-5 mt-8`}>
            <h3 className="text-lg font-semibold text-[#2A2D31] mb-4">
              Billing History
            </h3>
            {transactions.length > 0 ? (
              <div className="space-y-2.5">
                {transactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between rounded-[18px] border border-[#EEEEEE] bg-white px-3.5 py-3"
                  >
                    <div className="flex flex-col gap-1">
                      <div className="text-sm font-medium text-[#303236] capitalize">
                        {transaction.description}
                      </div>
                      <div className="text-xs text-[#8A8F94]">
                        {new Date(transaction.date).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          }
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-sm font-medium text-[#303236]">
                        ${transaction.amount.toFixed(2)}
                      </div>
                      {transaction.receiptUrl && (
                        <a
                          href={transaction.receiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#A0A4A8] hover:text-[#5E6268]"
                          title="View Receipt"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-[18px] border border-[#EEEEEE] bg-white px-3.5 py-3 text-sm text-[#777C82]">
                No billing history available
              </div>
            )}
          </div>
        </>
      )}

      {/* Modals - Only for BILLING and not on free plan */}
      {currentSubscription?.plan?.type !== "free" && (
        <div className="space-y-0">
          <AddPaymentModal
            isOpen={showAddPayment}
            onClose={() => { setShowAddPayment(false); fetchPaymentMethods(); }}
            customerName={customerName}
            customerEmail={customerEmail}
          />

          <DeleteConfirmationModal
            isOpen={deleteConfirmation.show}
            onClose={() =>
              setDeleteConfirmation({
                show: false,
                cardId: null,
                cardDetails: null,
              })
            }
            onConfirm={() => handleRemovePaymentMethod(deleteConfirmation.cardId)}
            cardDetails={deleteConfirmation.cardDetails}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(BillingTab);
